/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Link, useParams, useNavigate, useLocation} from 'react-router-dom'
import {PageTitle} from '../../../core'
import {KTSVG, detailPageUrl, DetailPage, SourceType} from '../../../helpers'
import 'flatpickr/dist/flatpickr.css'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import {KTCard, KTCardBody, DeleteModal, Notes} from '../../../components'
import {useDispatch, useSelector} from 'react-redux'
import {guaranteeletterGetId, guaranteeletterDelete} from '../../../redux/slices/guaranteeletter.slice'
import Dropdown from 'react-bootstrap/Dropdown'
import {useMemoizedPageUrl, useMemoizedBreadcrumbs} from '../../../utils/performanceOptimizations'

const selectGuaranteeLetterDetail = (state) => ({
  detail: state.guaranteeletter.detail,
  isDeleted: state.guaranteeletter.isDeleted,
  error: state.guaranteeletter.error
})

function Detail() {
  const [show, setShow] = useState(false)
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()
  const {detail, isDeleted, error} = useSelector(selectGuaranteeLetterDetail)
  const dispatch = useDispatch()
  const pageUrl = useMemoizedPageUrl(location)

  useEffect(() => {
    dispatch(guaranteeletterGetId(id))
  }, [dispatch, id])

  const handleDelete = useCallback(() => {
    dispatch(guaranteeletterDelete({id}))
  }, [dispatch, id])

  useEffect(() => {
    if (isDeleted) navigate(`/${pageUrl[1]}/${pageUrl[2]}`)
  }, [isDeleted, navigate, pageUrl])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.GUARATEELETTER'}),
      path: '/cash/guaranteeletter',
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.GUARATEELETTER.DETAIL'})}
      </PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-9'>
          {detail && (
            <>
              <KTCard className='mb-5'>
                <KTCardBody className='p-9'>
                  <div className='d-flex flex-wrap'>
                    <div className='flex-grow-1 w-100'>
                      <div className='d-flex justify-content-between align-items-start flex-wrap mb-5 row'>
                        <div className='d-flex flex-column col-xl-7'>
                          <div className='d-flex align-items-center mb-2'>
                            <div className='text-gray-900 fs-2 fw-bold me-1'>{detail && detail.name}</div>
                            <div className='ms-5'></div>
                          </div>
                          <div className='d-flex flex-wrap fw-semibold fs-6 mb-4 pe-2'>
                            <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                              <Link
                                to={detailPageUrl(DetailPage.Project, detail && detail.projectId)}
                                className='text-gray-400 text-hover-primary'
                                target='_blank'
                              >
                                <KTSVG
                                  path='/images/icons/nav/Projects.svg'
                                  className='svg-icon svg-icon-2 fs-4 me-1'
                                />
                                {detail && detail.projectName}
                              </Link>
                            </div>
                            <div className='d-flex align-items-center text-gray-400 me-5 mb-2'>
                              <Link
                                to={detailPageUrl(DetailPage.CompanyCustomer, detail && detail.companyId)}
                                className='text-gray-400 text-hover-primary'
                                target='_blank'
                              >
                                <KTSVG
                                  path='/images/icons/duotune/finance/fin006.svg'
                                  className='svg-icon svg-icon-2 fs-4 me-1'
                                />
                                {detail && detail.companyName}
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div className='d-flex gap-3 align-items-center col-xl-5 justify-content-end'>
                          <Link
                            to={`/${pageUrl[1]}/${pageUrl[2]}/edit/${id}`}
                            className='btn btn-sm btn-primary align-self-center'
                          >
                            <KTSVG
                              path='/images/icons/duotune/general/gen055.svg'
                              className='svg-icon-2'
                            />
                            {intl.formatMessage({id: 'BUTTON.EDIT'})}
                          </Link>
                          <Dropdown autoClose='outside' align={{lg: 'end'}}>
                            <Dropdown.Toggle
                              variant='secondary'
                              size='sm'
                              className='iconless ps-3 pe-3'
                            >
                              <KTSVG
                                path='/images/icons/duotune/general/gen053.svg'
                                className='svg-icon-2'
                              />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-rounded menu-gray-800 menu-state-bg-light-primary fw-semibold py-3'>
                              <Dropdown.Item
                                onClick={() => setShow(true)}
                                className='menu-item px-3'
                              >
                                <div className='menu-content text-muted pb-2 px-3 fs-7 text-uppercase'>
                                  {intl.formatMessage({id: 'BUTTON.DELETE'})}
                                </div>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    </div>
                    <div className='d-flex flex-wrap flex-stack'>
                      <div className='d-flex flex-column flex-grow-1 pe-8'>
                        <div className='d-flex flex-wrap'>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARATEELETTERTYPENAME'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail && detail.guaranteeLetterTypeName}</div>
                          </div>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARATEELETTERSTATENAME'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail && detail.guaranteeLetterStateName}</div>
                          </div>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARATEELETTERNO'})}
                            </div>
                            <div className='fw-bold fs-4'>{detail && detail.guaranteeLetterNo}</div>
                          </div>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.GUARANTEELETTEREXISTENCE'})}
                            </div>
                            <div className='fw-bold fs-4'>{(detail && detail.guaranteeLetterExistenceName) || '-'}</div>
                          </div>
                          <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                            <div className='fw-semibold fs-6 text-gray-400'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.DEFERMENTDATE'})}
                            </div>
                            <div className='fw-bold fs-4'>
                              {detail && detail.defermentDate
                                ? intl.formatDate(new Date(detail.defermentDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })
                                : '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </KTCardBody>
              </KTCard>
              <KTCard>
                <KTCardBody className='p-9'>
                  <Tabs className='nav-line-tabs' fill>
                    <Tab eventKey='general' title={intl.formatMessage({id: 'GENERAL'})}>
                      <div className='row p-5'>
                        <div className='col-lg-10 order-2 order-lg-1'>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.EDITDATE'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>
                                {detail && detail.editDate && intl.formatDate(new Date(detail.editDate), {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </span>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BENEFICIARY'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>{detail && detail.beneficiary}</span>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BANKNAME'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>{detail && detail.bankName}</span>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BRANCH'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>{detail && detail.branch}</span>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.COMMISSIONDATE'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              {detail && detail.commissionDate && (
                                <span className='fw-bold fs-6'>
                                  {intl.formatDate(new Date(detail.commissionDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.DELIVERYDATE'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              {detail && detail.deliveryDate && (
                                <span className='fw-bold fs-6'>
                                  {intl.formatDate(new Date(detail.deliveryDate), {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BALANCE'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>
                                {detail && detail.balance !== undefined && intl.formatNumber(detail.balance, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </span>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.ORIGINALAMOUNT'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>
                                {detail && detail.originalAmount !== undefined && intl.formatNumber(detail.originalAmount, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </span>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.BANKCOMISSION'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>
                                {detail && detail.bankCommission !== undefined && intl.formatNumber(detail.bankCommission, {
                                  style: 'currency',
                                  currency: 'try',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 4,
                                })}
                              </span>
                            </div>
                          </div>
                          <div className='row mb-7'>
                            <label className='col-lg-4 fw-bold text-muted'>
                              {intl.formatMessage({id: 'GUARATEELETTER.CREATE.FINANCIALRATE'})}
                            </label>
                            <div className='col-lg-8 fv-row'>
                              <span className='fw-bold fs-6'>{detail && detail.financialRate}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Tab>
                  </Tabs>
                </KTCardBody>
              </KTCard>
            </>
          )}
        </div>
        <div className='col-xl-3'>
          <Notes SourceId={Number(id)} SourceTypeId={SourceType.GuaranteeLetter} />
        </div>
      </div>
      <DeleteModal
        onClick={handleDelete}
        onOpen={show}
        onClose={() => setShow(false)}
        error={error}
      />
    </>
  )
}
export {Detail}
